import { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, RefreshCw, History, Search, Check, Edit3 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { STATUS_COLORS, STATUS_LABELS } from '../utils/constants';

function ActivityCombobox({ activities, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selected = activities.find(a => a.name === value);
  const filtered = search.trim()
    ? activities.filter(a =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        (a.service_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : activities;

  // Group by service
  const grouped = {};
  filtered.forEach(a => {
    const svc = a.service_name || 'Other';
    if (!grouped[svc]) grouped[svc] = [];
    grouped[svc].push(a);
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          data-testid="activity-search-input"
          className="pl-9 pr-3"
          placeholder={selected ? selected.name : "Search activity..."}
          value={open ? search : (selected ? selected.name : '')}
          onChange={e => { setSearch(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setSearch(''); }}
        />
      </div>
      {open && (
        <div ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[280px] overflow-y-auto"
          data-testid="activity-dropdown">
          {Object.keys(grouped).length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400">No activities found</div>
          ) : (
            Object.entries(grouped).map(([svc, items]) => (
              <div key={svc}>
                <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 sticky top-0">
                  {svc}
                </div>
                {items.map(a => (
                  <button key={a.id} type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-[#5B6FD6]/5 flex items-center justify-between transition-colors ${value === a.name ? 'bg-[#5B6FD6]/10 text-[#5B6FD6]' : 'text-slate-700'}`}
                    data-testid={`activity-option-${a.id}`}
                    onClick={() => { onChange(a); setSearch(''); setOpen(false); }}>
                    <span className="truncate">{a.name}</span>
                    {value === a.name && <Check className="h-4 w-4 text-[#5B6FD6] flex-shrink-0" />}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function CreateTaskDialog({ open, onClose, clients, users, services, canCreate, currentUser, onCreated }) {
  const isMember = currentUser?.role === 'member';
  const memberAdmin = isMember ? users.find(u => u.id === currentUser?.parent_admin_id) : null;
  const [formData, setFormData] = useState({ title: '', description: '', service_name: '', client_id: '', assignee_id: '', reviewer_id: '', deadline: '', is_recurring: false, recurrence_type: '', client_dependency: false, estimate_hours: '' });
  const [activities, setActivities] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  // Manual entry mode (Jun 2026): create the task + log the time worked in
  // one shot. Used to backfill tasks that were finished before they were
  // entered into the system.
  const [manualMode, setManualMode] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [manualNote, setManualNote] = useState('');
  const [manualMarkCompleted, setManualMarkCompleted] = useState(true);

  useEffect(() => {
    if (open && activities.length === 0) {
      api.get('/activities').then(res => setActivities(res.data)).catch(() => {});
    }
  }, [open, activities.length]);

  // Prefill sticky fields from the last successfully-created task so users
  // creating many similar tasks in a row don't repeat themselves. Title,
  // description, and deadline always start blank (per requirement).
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem('finaccpro_last_task');
      if (!raw) return;
      const last = JSON.parse(raw);
      setFormData(prev => ({
        ...prev,
        client_id: last.client_id || prev.client_id,
        assignee_id: last.assignee_id || prev.assignee_id,
        reviewer_id: last.reviewer_id || prev.reviewer_id,
        is_recurring: typeof last.is_recurring === 'boolean' ? last.is_recurring : prev.is_recurring,
        recurrence_type: last.recurrence_type || prev.recurrence_type,
      }));
    } catch { /* ignore malformed localStorage */ }
  }, [open]);

  // Auto-prefill assignee + reviewer for members
  useEffect(() => {
    if (open && isMember && currentUser?.id) {
      setFormData(prev => ({
        ...prev,
        assignee_id: currentUser.id,
        reviewer_id: currentUser.parent_admin_id || prev.reviewer_id,
      }));
    }
  }, [open, isMember, currentUser?.id, currentUser?.parent_admin_id]);

  const resetForm = () => {
    setFormData({
      title: '', description: '', service_name: '', client_id: '',
      assignee_id: isMember ? (currentUser?.id || '') : '',
      reviewer_id: isMember ? (currentUser?.parent_admin_id || '') : '',
      deadline: '', is_recurring: false, recurrence_type: '', client_dependency: false, estimate_hours: ''
    });
    setDuplicateWarning(null);
    setClientHistory([]);
    setShowDuplicateDialog(false);
    setManualMode(false);
    setManualHours('');
    setManualMinutes('');
    setManualNote('');
    setManualMarkCompleted(true);
    setManualDate(new Date().toISOString().slice(0, 10));
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleActivitySelect = (activity) => {
    setFormData(prev => ({
      ...prev,
      title: activity.name,
      service_name: activity.service_name || '',
    }));
  };

  const handleClientChange = useCallback(async (clientId) => {
    setFormData(prev => ({ ...prev, client_id: clientId }));
    if (clientId) {
      try { const res = await api.get(`/tasks/client-history/${clientId}`); setClientHistory(res.data); } catch { setClientHistory([]); }
    } else { setClientHistory([]); }
    setDuplicateWarning(null);
  }, []);

  useEffect(() => {
    if (formData.client_id && formData.service_name) {
      api.post('/tasks/check-duplicate', { client_id: formData.client_id, service_name: formData.service_name })
        .then(res => setDuplicateWarning(res.data.has_duplicates ? res.data.similar_tasks : null))
        .catch(() => setDuplicateWarning(null));
    }
  }, [formData.client_id, formData.service_name]);

  const submitTask = async () => {
    try {
      const payload = { ...formData };
      if (!payload.is_recurring) payload.recurrence_type = null;
      if (payload.reviewer_id === 'none' || !payload.reviewer_id) payload.reviewer_id = null;
      payload.estimate_hours = payload.estimate_hours ? parseFloat(payload.estimate_hours) : null;
      const res = await api.post('/tasks', payload);
      const createdTask = res.data;

      // Manual entry: log time + optionally mark completed in one shot. Used
      // for back-filling tasks that were done before being entered into the
      // system. Errors here are non-fatal — the task itself was already
      // created successfully.
      let manualTimeLogged = false;
      if (manualMode) {
        const h = parseFloat(manualHours || 0);
        const m = parseFloat(manualMinutes || 0);
        const totalHours = h + (m / 60);
        if (totalHours > 0) {
          try {
            await api.post(`/tasks/${createdTask.id}/time-log`, {
              hours: totalHours,
              description: manualNote || `Manual entry for ${manualDate}`,
            });
            manualTimeLogged = true;
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Task created but time log failed');
          }
        }
        if (manualMarkCompleted && manualTimeLogged) {
          try {
            await api.put(`/tasks/${createdTask.id}/status`, { status: 'completed' });
          } catch (e) {
            // Reviewer-only completion may 403 — that's expected and fine.
            // The time-log still went through.
          }
        }
      }

      // Remember the "sticky" fields for the next task creation
      try {
        localStorage.setItem('finaccpro_last_task', JSON.stringify({
          client_id: formData.client_id,
          assignee_id: formData.assignee_id,
          reviewer_id: formData.reviewer_id,
          is_recurring: formData.is_recurring,
          recurrence_type: formData.recurrence_type,
        }));
      } catch { /* localStorage quota / disabled — non-fatal */ }
      toast.success(manualTimeLogged ? 'Task created & time logged' : 'Task created');
      resetForm();
      onClose();
      onCreated();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create task'); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (duplicateWarning?.length > 0) { setShowDuplicateDialog(true); return; }
    submitTask();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Create New Task</DialogTitle>
            <DialogDescription className="sr-only">Create a new task</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Activity <span className="text-red-600">*</span></Label>
                <ActivityCombobox activities={activities} value={formData.title}
                  onChange={handleActivitySelect} />
                {formData.service_name && (
                  <p className="text-xs text-[#5B6FD6] mt-1" data-testid="auto-service-label">
                    Service: <span className="font-medium">{formData.service_name}</span>
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <Label>Description <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                <Textarea data-testid="task-description-input" placeholder="Add notes or details..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
              </div>
              <div>
                <Label>Client <span className="text-red-600">*</span></Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {canCreate && (
                <div>
                  <Label>Executor <span className="text-red-600">*</span></Label>
                  <Select value={formData.assignee_id} onValueChange={(v) => setFormData({ ...formData, assignee_id: v })}>
                    <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select executor" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {canCreate && (
                <div>
                  <Label>Reviewer</Label>
                  <Select value={formData.reviewer_id} onValueChange={(v) => setFormData({ ...formData, reviewer_id: v })}>
                    <SelectTrigger data-testid="task-reviewer-select"><SelectValue placeholder="Select reviewer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Reviewer</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Deadline <span className="text-red-600">*</span></Label>
                <Input data-testid="task-deadline-input" type="datetime-local" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} min={new Date().toISOString().slice(0, 16)} required />
              </div>
              <div>
                <Label>Estimate (hours) <span className="text-slate-400 font-normal">— for variance tracking</span></Label>
                <Input
                  data-testid="task-estimate-input"
                  type="number" min="0" step="0.25"
                  placeholder="e.g. 2.5"
                  value={formData.estimate_hours}
                  onChange={(e) => setFormData({ ...formData, estimate_hours: e.target.value })}
                />
              </div>
            </div>

            {duplicateWarning?.length > 0 && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="duplicate-warning-banner">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-amber-600" /><span className="font-semibold text-amber-800 text-sm">Similar task exists</span></div>
                {duplicateWarning.map(t => (
                  <div key={t.id} className="text-sm text-amber-700 bg-white rounded px-3 py-2 mb-1 border border-amber-200">{t.title} - {t.assignee_name} ({t.status})</div>
                ))}
              </div>
            )}

            {formData.client_id && clientHistory.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="client-task-history">
                <div className="flex items-center gap-2 mb-2"><History className="h-4 w-4 text-[#5B6FD6]" /><span className="font-semibold text-slate-700 text-sm">Client History ({clientHistory.length})</span></div>
                {clientHistory.slice(0, 5).map(t => (
                  <div key={t.id} className="text-sm text-slate-600 py-1">{t.title} - <Badge className={`text-xs ${STATUS_COLORS[t.status]} text-white`}>{STATUS_LABELS[t.status]}</Badge></div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t">
              <Checkbox checked={manualMode} onCheckedChange={(c) => setManualMode(!!c)} data-testid="task-manual-checkbox" />
              <Label className="text-sm"><Edit3 className="h-3 w-3 inline mr-1" />Manual entry (log hours worked + optionally mark completed)</Label>
            </div>
            {manualMode && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="manual-entry-block">
                <p className="text-xs text-slate-500">For back-filling a task that was already worked on. The task will be created and the hours below will be logged against it.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} data-testid="manual-entry-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Hours</Label>
                    <Input type="number" min="0" step="0.5" placeholder="0" value={manualHours} onChange={(e) => setManualHours(e.target.value)} data-testid="manual-entry-hours" />
                  </div>
                  <div>
                    <Label className="text-xs">Minutes</Label>
                    <Input type="number" min="0" max="59" step="1" placeholder="0" value={manualMinutes} onChange={(e) => setManualMinutes(e.target.value)} data-testid="manual-entry-minutes" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note (optional)</Label>
                  <Textarea rows={2} placeholder="What was done…" value={manualNote} onChange={(e) => setManualNote(e.target.value)} data-testid="manual-entry-note" />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={manualMarkCompleted} onCheckedChange={(c) => setManualMarkCompleted(!!c)} data-testid="manual-entry-complete-toggle" />
                  <Label className="text-xs">Mark task as completed after logging</Label>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t">
              <Checkbox checked={formData.is_recurring} onCheckedChange={(c) => setFormData({ ...formData, is_recurring: c, recurrence_type: c ? formData.recurrence_type : '' })} data-testid="task-recurring-checkbox" />
              <Label className="text-sm"><RefreshCw className="h-3 w-3 inline mr-1" />Recurring task</Label>
              {formData.is_recurring && (
                <Select value={formData.recurrence_type} onValueChange={(v) => setFormData({ ...formData, recurrence_type: v })}>
                  <SelectTrigger className="w-36 ml-2" data-testid="task-recurrence-select"><SelectValue placeholder="Interval" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="bi_monthly">Bi-Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="submit" className={duplicateWarning?.length > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#5B6FD6] hover:bg-[#4a5bc5]'} data-testid="task-submit-button"
                disabled={!formData.title || !formData.service_name || !formData.client_id || !formData.deadline || (canCreate && !formData.assignee_id)}>
                {duplicateWarning?.length > 0
                  ? 'Create Anyway'
                  : manualMode
                    ? 'Create & Log Time'
                    : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md" data-testid="duplicate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Similar Task Exists</DialogTitle>
            <DialogDescription>A similar task already exists. Create anyway?</DialogDescription>
          </DialogHeader>
          {duplicateWarning?.map(t => (
            <div key={t.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm">
              <div className="font-medium">{t.title}</div>
              <div className="text-slate-600 mt-1">{t.client_name} - {t.assignee_name} ({t.status})</div>
            </div>
          ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={submitTask} data-testid="duplicate-create-anyway-btn">Create Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
